// Actions
import {
  setResourceLoaded, setResourcePending, setResourceUnloaded
} from 'loading';

// Selectors
import {
  getPendingResourcePromise,
  isResourceTypeLoaded,
  isResourceTypePending,
  isResourceTypeUnloaded,
  isResourcePending
} from 'loading/selectors';

// Util
import { errorLevel } from 'constants/errors';
import { logError } from 'api/errors';
import { neverResolve } from 'util/promise';
import generateUuid from 'uuid/v4';


const loadExclusiveResource = ({ getState, dispatch, type, id, onError, fetcher, processor }) => {
  const pendingPromise = fetcher();
  // Signal any other pending requests of this type to crawl off and die
  dispatch(setResourcePending({ type, id, promise: pendingPromise, exclusive: true }));
  return pendingPromise.then( raw => {
    if(!isResourcePending({ state: getState(), type, id })) {
      // we have been told to crawl off and die
      return neverResolve();
    }
    dispatch(setResourceLoaded({ type, id }));
    return processor(raw);
  })
  .catch( err => {
    dispatch(setResourceUnloaded({ type, id}));
    if(onError) {
      return onError(err);
    }
    // Caller will have to handle errors
    return Promise.reject(err);
  });
};


// This is a core finder utility that most other finders will call,
// as opposed to a finder for a specific resource itself
export const findResource = ({
  getState,
  dispatch,
  forceReload=false,
  forceWait=false,
  onError,
  type,
  id=generateUuid(),
  selector,
  fetcher,
  processor
}) => { // NOSONAR Many different scenarios to cover with not an easy break down to seperate
  if(!type) {
    return Promise.reject('Resource type is required');
  }
  return Promise.resolve() // force a thenable wrapper
  .then( () => { // NOSONAR Many different scenarios to cover with not an easy break down to seperate
    const state = getState();
    // Do we use existing value?
    if( !forceReload && isResourceTypeLoaded({ state, type, id }) ) {
      return selector();
    }
    // Do we return existing promise?
    if( !forceReload && forceWait && isResourceTypePending({ state, type, id }) ) {
      return getPendingResourcePromise({ state, type });
    }
    // Do we return default data while previously requested actual data is still pending?
    if( !forceReload && !forceWait && isResourceTypePending({ state, type, id }) ) {
      return selector();
    }
    // Do we request fresh and return pending promise?
    if( forceWait && (forceReload || isResourceTypeUnloaded({ state, type, id }) ) ) {
      return loadExclusiveResource({ getState, dispatch, type, id, onError, fetcher, processor });
    }
    // Do we request fresh in the background and return default data?
    if( !forceWait && (forceReload || isResourceTypeUnloaded({ state, type, id }) ) ) {
      loadExclusiveResource({ getState, dispatch, type, id, onError, fetcher, processor })
      .catch( err => {
        if(onError) {
          onError(err);
        } else {
          logError({ severity: errorLevel.WARN, andHandleNow: true }); // Caller wouldn't get this async error
        }
      });
      return selector();
    }
    return Promise.reject('Invalid findResource condition');
  });
};
